import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
} from "@mui/material";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import { adminListPayins } from "../api";

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt)
    ? "—"
    : dt.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
};

export default function AdminPayins() {
  const [payins, setPayins] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchPayins = useCallback(() => {
    setLoading(true);
    adminListPayins({ page, limit: 20 })
      .then(({ data }) => {
        setPayins(data.payins || []);
        setTotal(data.pagination?.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => {
    fetchPayins();
  }, [fetchPayins]);

  const statusColor = (s) => {
    if (s === "success") return "success";
    if (s === "failed" || s === "expired") return "error";
    if (s === "initiated") return "info";
    return "warning";
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            All Payins
          </Typography>
          <Typography variant="body2" color="text.secondary">
            System-wide deposit history
          </Typography>
        </Box>
      </Stack>

      <Card sx={{ overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : payins.length === 0 ? (
          <Box sx={{ p: 5, textAlign: "center" }}>
            <CreditCardRoundedIcon
              sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
            />
            <Typography color="text.secondary">No payins found</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Net Credit</TableCell>
                <TableCell>Commission</TableCell>
                <TableCell>Gateway</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payins.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {p.first_name} {p.last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.email}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    ₹{Number(p.amount || 0).toLocaleString("en-IN")}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={p.status}
                      color={statusColor(p.status)}
                      sx={{ fontWeight: 600, fontSize: 11 }}
                    />
                  </TableCell>
                  <TableCell>
                    {p.net_amount != null
                      ? `₹${Number(p.net_amount).toLocaleString("en-IN")}`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {p.total_commission != null
                      ? `₹${Number(p.total_commission).toLocaleString("en-IN")}`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {p.gateway_order_id ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace", fontSize: 11 }}
                      >
                        {p.gateway_order_id.slice(0, 16)}...
                      </Typography>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
                    {formatDate(p.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Box>
        )}
        {Math.ceil(total / 20) > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination
              count={Math.ceil(total / 20)}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Box>
        )}
      </Card>
    </Box>
  );
}
